import { downloadFromUrl } from '@helpers/downloadFromUrl';
import { handleDownloadError, triggerBrowserDownload } from '@helpers/jobLinkHelpers';
import { sanitizeFileName } from '@helpers/sanitizeFileName';
import { useAPI } from '@providers/useAPI';
import { useMutation } from '@tanstack/react-query';
import { MediaService } from 'app/API';
import { useCallback } from 'react';
import { findServerMediaFileName } from '../helpers/findServerMediaFileName';

type TMediaMeta = { name?: string; extra?: { mediaId?: string } };

export const useDownloadJobMedia = (jobId: string) => {
  const { call } = useAPI();

  const { mutateAsync } = useMutation({
    mutationFn: async (meta: TMediaMeta) => {
      const mediaId = meta.extra?.mediaId;
      const candidates = new Set<string>();
      if (meta.name) {
        candidates.add(meta.name);
        candidates.add(sanitizeFileName(meta.name));
      }
      if (mediaId) {
        const list = await call(() => MediaService.getFilesList({ jobId }));
        const serverName = findServerMediaFileName(list?.media ?? [], String(mediaId));
        if (serverName) {
          candidates.add(serverName);
          candidates.add(sanitizeFileName(serverName));
        }
      }
      for (const file of candidates) {
        if (!file) {
          continue;
        }
        const signed = await call(() =>
          MediaService.downloadSignedMediaFile({ jobId, file, disposition: 'attachment' }),
        );
        if (signed?.signedUrl) {
          triggerBrowserDownload(signed.signedUrl, meta.name ?? file);
          return;
        }
      }
      if (mediaId) {
        await downloadFromUrl(`${window.location.origin}/api/media/${mediaId}/download`, meta.name ?? 'audio');
        return;
      }
      throw new Error('Signed URL is missing');
    },
    onError: handleDownloadError,
  });

  return useCallback(
    (meta: TMediaMeta) =>
      mutateAsync(meta).then(
        () => undefined,
        () => undefined,
      ),
    [mutateAsync],
  );
};
